import btoa from 'btoa';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { css } from 'styled-components';
import { LAYERS, MEDIA_QUERY_BREAKPOINTS, breakpoints } from './react.types';
export const getShadowStyle = ({ theme, depth = 1, }) => {
    const absoluteDepth = Math.abs(depth);
    const propertyKey = `EffectElevation${absoluteDepth}${depth > 0 ? 'Bottom' : 'Top'}`;
    const offsetXString = `${propertyKey}0OffsetX`;
    const offsetYString = `${propertyKey}0OffsetY`;
    const radiusString = `${propertyKey}0Radius`;
    const spreadString = `${propertyKey}0Spread`;
    const colorString = `${propertyKey}0Color`;
    const tenOffsetXString = `${propertyKey}1OffsetX`;
    const tenOffsetYString = `${propertyKey}1OffsetY`;
    const oneRadiusString = `${propertyKey}1Radius`;
    const oneSpreadString = `${propertyKey}1Spread`;
    const oneColorString = `${propertyKey}1Color`;
    const offsetX = theme[offsetXString];
    const offsetY = theme[offsetYString];
    const radius = theme[radiusString];
    const spread = theme[spreadString];
    const color = theme[colorString];
    const tenOffsetX = theme[tenOffsetXString];
    const tenOffsetY = theme[tenOffsetYString];
    const oneRadius = theme[oneRadiusString];
    const oneSpread = theme[oneSpreadString];
    const oneColor = theme[oneColorString];
    return `${offsetX}px ${offsetY}px ${radius}px ${spread}px ${color}, ${tenOffsetX}px ${tenOffsetY}px ${oneRadius}px ${oneSpread}px ${oneColor}`;
};
export function createIdFromString(str) {
    return str.toLowerCase().replace(/ /g, '-');
}
export const getPluralString = (number, translationSingular, translationPlural) => (number === 1 ? translationSingular : translationPlural);
export function useDisclosure(isOpenDefault = false) {
    const [isOpen, setIsOpen] = useState(isOpenDefault);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((state) => !state), []);
    return { isOpen, open, close, toggle };
}
export const useKeypress = (key, action) => {
    useEffect(() => {
        function onKeyup(e) {
            if (e.key === key)
                action();
        }
        window.addEventListener('keyup', onKeyup);
        return () => window.removeEventListener('keyup', onKeyup);
    }, []);
};
export const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};
export function useWindowSize(triggeredBy) {
    const isClient = typeof window === 'object';
    const lastWidth = useRef(0);
    const lastHeight = useRef(0);
    function getSize() {
        return {
            width: isClient ? window.innerWidth : undefined,
            height: isClient ? window.innerHeight : undefined,
        };
    }
    const [windowSize, setWindowSize] = useState(getSize);
    function handleResize() {
        if (!isClient) {
            return false;
        }
        const isTriggeredByWidth = triggeredBy === 'width' && window?.innerWidth !== lastWidth.current;
        const isTriggeredByHeight = triggeredBy === 'height' && window?.innerHeight !== lastHeight.current;
        if (isTriggeredByWidth || isTriggeredByHeight || !triggeredBy) {
            const { width, height } = getSize();
            lastWidth.current = width;
            lastHeight.current = height;
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
    }
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
}
/**
 * Get the window height and rerender whenever it changes.
 * USE WITH CAUTION!
 * Responsive layouts should be implemented with CSS. Otherwise it will most likely add CLS.
 *
 * @param unsafeDefaultToWindowHeight Use the window height as the default value. This can only be set to `true` when not used in a server-rendered path. Otherwise it will break the hydration.
 * @returns height of the window
 */
export function useWindowHeight(unsafeDefaultToWindowHeight) {
    const [windowHeight, setWindowHeight] = useState(unsafeDefaultToWindowHeight && typeof window !== 'undefined'
        ? window.innerHeight
        : undefined);
    useEffect(() => {
        function handleResize() {
            if (window.innerHeight !== windowHeight) {
                setWindowHeight(window.innerHeight);
            }
        }
        window.addEventListener('resize', handleResize);
        !unsafeDefaultToWindowHeight && handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowHeight;
}
/**
 * Get the window width and rerender whenever it changes.
 * USE WITH CAUTION!
 * Responsive layouts should be implemented with CSS. Otherwise it will most likely add CLS.
 *
 * @param unsafeDefaultToWindowWidth Use the window width as the default value. This can only be set to `true` when not used in a server-rendered path. Otherwise it will break the hydration.
 * @returns width of the window
 */
export function useWindowWidth(unsafeDefaultToWindowWidth) {
    const [windowWidth, setWindowWidth] = useState(unsafeDefaultToWindowWidth && typeof window !== 'undefined'
        ? window.innerWidth
        : undefined);
    useEffect(() => {
        function handleResize() {
            if (window.innerWidth !== windowWidth) {
                setWindowWidth(window.innerWidth);
            }
        }
        window.addEventListener('resize', handleResize);
        !unsafeDefaultToWindowWidth && handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowWidth;
}
export function debounce(fn, wait) {
    let cancel = () => { };
    const wrapFunc = (...args) => {
        cancel();
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => Promise.resolve(fn(...args))
                .then(resolve)
                .catch(reject), wait);
            cancel = () => {
                clearTimeout(timer);
            };
        });
    };
    return wrapFunc;
}
export const universalBtoa = (content) => typeof window !== 'undefined' ? window.btoa(content) : btoa(content);
export const buildResponsiveValueCss = ({ property, values, evaluate, }) => {
    const valueIsArray = Array.isArray(values);
    const defaultValue = valueIsArray ? evaluate(values[0]) : evaluate(values);
    const breakpointValues = valueIsArray ? values.slice(1) : null;
    const breakpointsCss = valueIsArray
        ? breakpointValues
            .map((value, index) => `
            @media screen and (min-width: ${breakpoints[index]}px) {
              ${property}: ${evaluate(value)};
            }
          `)
            .join('')
        : '';
    return `
    ${property}: ${defaultValue};

    ${breakpointsCss}
  `;
};
export const generateMediaQueries = (mobileFirst = true) => Object.keys(MEDIA_QUERY_BREAKPOINTS).reduce((prev, curr) => {
    const currentMediaQuery = (...styles) => `@media screen and (${mobileFirst ? 'min-width' : 'max-width'}: ${MEDIA_QUERY_BREAKPOINTS[curr]}px) {
      ${css(...styles)}
  }`;
    return {
        ...prev,
        [curr]: currentMediaQuery,
    };
}, { mobile: () => [], tablet: () => [], desktop: () => [] });
export const mediaQueries = generateMediaQueries();
export const mediaQueriesDesktopFirst = generateMediaQueries(false);
export const setToLayer = (layer) => `z-index: ${LAYERS[layer]};`;
export const pxToRems = (px) => {
    const numPx = typeof px === 'string' ? parseInt(px) : px;
    return `${numPx / 16}rem`;
};
export const pxToRemsResponsive = (px) => {
    if (!px) {
        return px?.toString();
    }
    return Array.isArray(px)
        ? px.map((p) => pxToRems(p))
        : pxToRems(px);
};
export const pxToRelative = (px) => {
    const numPx = typeof px === 'string' ? parseInt(px) : px;
    return numPx / 16;
};
export const getUniqueFontWeights = (theme) => {
    const themeKeys = Object.keys(theme);
    const filteredSet = new Set(themeKeys
        .filter((propertyName) => propertyName.includes('FontTextText') &&
        propertyName.includes('Weight') &&
        typeof propertyName === 'number')
        .map((propertyName) => theme[propertyName]));
    return Array.from(filteredSet)
        .sort((n1, n2) => n1 - n2)
        .join(';');
};
/**
 * Adds or changes opacity value for CSS rgb/rgba color syntax
 * @param {string} rgb - CSS rgb/rgba rgb(12, 34, 56) or rgba(12, 34, 56, 0.7)
 * @param {string|number} opacity - New opacity value <= 1
 * @returns {string} Returns CSS rgba syntax with added or changed opacity value
 */
export const rgbOpacity = (rgb, opacity) => {
    return rgb?.replace(/rgba?\((\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*)((?:,\s*[0-9.]*\s*)?)\)/, `rgba($1, ${opacity})`);
};
export const compareASC = (a, b) => {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
};
/**
 * Handle responsive props values and media queries in a consistent way
 */
export function handleResponsiveCssValues(...values) {
    const definedProperties = values.filter(({ value }) => !!value);
    const singleValues = definedProperties.filter(({ value }) => !Array.isArray(value));
    const arrayValues = definedProperties.filter(({ value }) => Array.isArray(value));
    const singleValueCss = singleValues
        .concat(arrayValues.map(({ cssProperty, value }) => ({
        cssProperty,
        value: value[2],
    })))
        .map(({ cssProperty, value }) => `${cssProperty}: ${value};`)
        .join('');
    if (!arrayValues.length) {
        return singleValueCss;
    }
    return `
    ${singleValueCss}

    @media (max-width: ${breakpoints[0]}px) {
      ${arrayValues
        .map(({ cssProperty, value }) => `${cssProperty}: ${value[0]};`)
        .join('')}
    }

    @media (min-width: ${breakpoints[0] + 1}px) and (max-width: ${breakpoints[1]}px) {
      ${arrayValues
        .map(({ cssProperty, value }) => `${cssProperty}: ${value[1]};`)
        .join('')}
    }
  `;
}
// Filter out 3rd party voilations from swiper, as we can't fix them
export const filterThirdPartyAXEViolations = (results) => {
    return results.violations = results.violations.filter(violation => violation.id !== 'aria-valid-attr-value' &&
        violation.id !== 'nested-interactive' &&
        !violation.nodes.map(node => node.html).join('').includes('aria-controls="swiper-wrapper-'));
};
//# sourceMappingURL=react.js.map